import { Badge } from '@/components/ui/badge';
import { Hash } from 'lucide-react';
import { useGetList } from 'ra-core';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { generateNextReference, PREFIX_MAP as REFERENCE_PREFIX_MAP } from './documentReference';
import { FormField, FormLabel } from '@/components/admin/form';

type DocType =
    | 'quotation'
    | 'proforma_invoice'
    | 'purchase_order'
    | 'customer_invoice'
    | 'delivery_challan'
    | 'sales_order'
    | 'goods_receipt'
    | 'enquiry'
    | 'opportunity'
    | 'project'
    | 'bom'
    | 'work_order'
    | 'vendor_bill'
    | 'design_request';

const RESOURCE_MAP: Record<DocType, string> = {
    quotation: 'quotations',
    proforma_invoice: 'proforma_invoices',
    purchase_order: 'purchase_orders',
    customer_invoice: 'customer_invoices',
    delivery_challan: 'delivery_challans',
    sales_order: 'sales_orders',
    goods_receipt: 'goods_receipts',
    enquiry: 'enquiries',
    opportunity: 'opportunities',
    project: 'projects',
    bom: 'boms',
    work_order: 'work_orders',
    vendor_bill: 'vendor_bills',
    design_request: 'design_requests',
};

const LABEL_MAP: Record<DocType, string> = {
    quotation: 'Quotation No.',
    proforma_invoice: 'Proforma Invoice No.',
    purchase_order: 'PO No.',
    customer_invoice: 'Invoice No.',
    delivery_challan: 'Challan No.',
    sales_order: 'Sales Order No.',
    goods_receipt: 'GRN No.',
    enquiry: 'Enquiry No.',
    opportunity: 'Opportunity No.',
    project: 'Project No.',
    bom: 'BoM Reference',
    work_order: 'WO Reference',
    vendor_bill: 'Bill Number',
    design_request: 'Design Request No.',
};

interface AutoReferenceFieldProps {
    docType: DocType;
    /** Optional override to pass the existing reference value (when editing). Skips auto-generation. */
    existingValue?: string;
}

/**
 * AutoReferenceField
 *
 * Industry-standard pattern: reference numbers are auto-generated
 * and displayed as read-only. Users cannot manually edit them.
 *
 * - On Create: Fetches last used references, generates next sequential number,
 *   writes it into the form field, and shows it as a styled badge.
 * - On Edit: Shows the existing reference as read-only.
 */
export const AutoReferenceField = ({ docType, existingValue }: AutoReferenceFieldProps) => {
    const { setValue, watch, register } = useFormContext();
    const currentRef = watch('reference');

    // Fetch existing records when creating (no existingValue). Filtered to
    // this doc type's own reference prefix — fetching just the single
    // latest-by-id row (regardless of format) let a differently-formatted
    // reference from another source (e.g. the auto-invoice edge function's
    // INV/2026-27/xxxx format) mask the real max sequence and reset
    // numbering back to 001, colliding with an existing record.
    const { data: existingRecords, isPending, error } = useGetList<{ id: number; reference: string }>(
        RESOURCE_MAP[docType],
        {
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'id', order: 'DESC' },
            filter: { 'reference@ilike': `${REFERENCE_PREFIX_MAP[docType as keyof typeof REFERENCE_PREFIX_MAP]}%` },
        },
        { enabled: !existingValue }
    );

    const hasGenerated = useRef(false);

    useEffect(() => {
        // If editing, set the existing value once and stop
        if (existingValue) {
            setValue('reference', existingValue, { shouldValidate: true });
            return;
        }

        // Already has a reference in the form — nothing to do
        if (currentRef) return;

        // Wait only while the query is genuinely in flight. If it errored or
        // settled with no data we still generate (from an empty list) so the
        // field never gets stuck on "Generating…". See bug tracker QTN #14.
        if (isPending && !error) return;

        // Already generated once this mount — don't generate again
        if (hasGenerated.current) return;

        hasGenerated.current = true;
        const refs = (existingRecords || [])
            .map((r) => r.reference)
            .filter(Boolean) as string[];

        const generated = generateNextReference(refs, docType as any);
        setValue('reference', generated, { shouldValidate: true, shouldDirty: true });
    }, [existingRecords, existingValue, currentRef, docType, setValue, isPending, error]);

    const label = LABEL_MAP[docType];

    return (
        <FormField id="reference" name="reference" className="w-full">
            <FormLabel className="text-sm font-medium text-muted-foreground">
                {label}
            </FormLabel>
            <div className="flex items-center gap-2 h-10 px-3 rounded-md border bg-[#ffffff] border-[#94a3b8] shadow-xs">
                <Hash className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="font-mono font-semibold text-sm text-foreground tracking-wide">
                    {currentRef || (
                        <span className="text-muted-foreground italic font-normal animate-pulse">
                            Generating…
                        </span>
                    )}
                </span>
                <Badge
                    variant="outline"
                    className="ml-auto text-[10px] text-muted-foreground border-border bg-card"
                >
                    Auto
                </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
                Auto-generated · Read only
            </p>
            {/* Formally register the hidden field with React Hook Form */}
            <input type="hidden" {...register('reference')} />
        </FormField>
    );
};
